import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "../../../config/firebaseConfig";
import { AI_LAB_COURSES, AVAILABLE_DAYS, TIME_SLOTS } from "../../dashboard/constants/ai-lab-constants";
import { LogOut, Plus, Trash2, Video, Users, Calendar, Clock, Pencil, X } from "lucide-react";

const BatchManager = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [courseId, setCourseId] = useState("");
  const [day, setDay] = useState("");
  const [timeSlot, setTimeSlot] = useState("");
  const [teacherName, setTeacherName] = useState("");
  const [meetLink, setMeetLink] = useState("");
  const [editingBatchId, setEditingBatchId] = useState(null);

  // Check Auth
  useEffect(() => {
    const isTeacher = sessionStorage.getItem("isTeacher");
    if (!isTeacher) {
      navigate("/teacher-panel/login");
    }
  }, [navigate]);

  // Fetch Batches
  const fetchBatches = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "ai-lab-batches"));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setBatches(list);
    } catch (err) {
      console.error("Error fetching batches:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem("isTeacher");
    navigate("/teacher-panel/login");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const batchData = {
        courseId,
        day,
        timeSlot,
        teacherName,
        meetLink,
      };

      if (editingBatchId) {
        await updateDoc(doc(db, "ai-lab-batches", editingBatchId), batchData);
        setEditingBatchId(null);
      } else {
        await addDoc(collection(db, "ai-lab-batches"), {
          ...batchData,
          createdAt: new Date()
        });
      }

      // Reset form
      setCourseId("");
      setDay("");
      setTimeSlot("");
      setTeacherName("");
      setMeetLink("");

      // Refresh list
      fetchBatches();
    } catch (err) {
      console.error("Error saving batch:", err);
      alert(`Failed to ${editingBatchId ? "update" : "create"} batch. Check console.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this batch link?")) {
      try {
        await deleteDoc(doc(db, "ai-lab-batches", id));
        setBatches(batches.filter(b => b.id !== id));
      } catch (err) {
        console.error("Error deleting batch:", err);
      }
    }
  };

  const handleEdit = (batch) => {
    setEditingBatchId(batch.id);
    setCourseId(batch.courseId || "");
    setDay(batch.day || "");
    setTimeSlot(batch.timeSlot || "");
    setTeacherName(batch.teacherName || "");
    setMeetLink(batch.meetLink || "");
  };

  const handleCancelEdit = () => {
    setEditingBatchId(null);
    setCourseId("");
    setDay("");
    setTimeSlot("");
    setTeacherName("");
    setMeetLink("");
  };

  const getCourseName = (id) => {
    return AI_LAB_COURSES.find(c => c.id === id)?.label || id;
  };
  const getTimeLabel = (id) => {
    return TIME_SLOTS.find(t => t.id === id)?.label || id;
  };

  if (!sessionStorage.getItem("isTeacher")) return null;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
          <div>
            <h1 className="text-2xl font-black text-slate-800">Teacher Batch Manager</h1>
            <p className="text-slate-500 font-medium text-sm">Assign Google Meet links to AI Lab schedules.</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* List of Batches */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 h-full min-h-[500px]">
              <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Video className="w-5 h-5 text-indigo-600" /> Active Batches
              </h2>

              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="w-8 h-8 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
                </div>
              ) : batches.length === 0 ? (
                <div className="text-center py-12 text-slate-500 font-medium">
                  No active batches found. Create one to get started.
                </div>
              ) : (
                <div className="grid gap-4">
                  {batches.map(batch => {
                    const isEditing = batch.id === editingBatchId;
                    return (
                      <div
                        key={batch.id}
                        className={`p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all duration-300 ${
                          isEditing
                            ? "border-indigo-400 bg-indigo-50/40 ring-2 ring-indigo-500/10 shadow-sm"
                            : "border-slate-200 hover:border-blue-200 bg-slate-50/50"
                        }`}
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-black text-slate-800 text-lg">{getCourseName(batch.courseId)}</span>
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full">BATCH</span>
                          </div>
                          <div className="flex items-center gap-4 text-xs font-semibold text-slate-500">
                            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {batch.day}</span>
                            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {getTimeLabel(batch.timeSlot)}</span>
                            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {batch.teacherName}</span>
                          </div>
                          <div className="pt-2">
                            <a href={batch.meetLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-xs font-bold hover:underline break-all">
                              {batch.meetLink}
                            </a>
                          </div>
                        </div>
                        <div>
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-lg border border-slate-200 text-slate-400 font-bold text-xs animate-pulse">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                                Editing
                              </span>
                              <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="p-1.5 hover:bg-slate-100 border border-slate-200 hover:border-slate-300 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
                                title="Cancel Edit"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleEdit(batch)}
                                className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                                title="Edit Batch"
                              >
                                <Pencil className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(batch.id)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete Batch"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Create Form */}
          <div className="lg:col-span-1">
            <div className={`bg-white rounded-2xl shadow-sm border p-6 transition-all duration-300 ${
              editingBatchId
                ? "border-indigo-300 ring-4 ring-indigo-500/5 shadow-indigo-100/50"
                : "border-slate-200"
            }`}>
              <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                {editingBatchId ? (
                  <>
                    <Pencil className="w-5 h-5 text-indigo-600" /> Edit Batch
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5 text-blue-600" /> Create New Batch
                  </>
                )}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Course</label>
                  <select required value={courseId} onChange={e => setCourseId(e.target.value)} className="w-full p-2.5 rounded-xl border border-slate-200 focus:border-blue-500 outline-none">
                    <option value="">Select Course...</option>
                    {AI_LAB_COURSES.map(c => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Day</label>
                  <select required value={day} onChange={e => setDay(e.target.value)} className="w-full p-2.5 rounded-xl border border-slate-200 focus:border-blue-500 outline-none">
                    <option value="">Select Day...</option>
                    {AVAILABLE_DAYS.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Time Slot</label>
                  <select required value={timeSlot} onChange={e => setTimeSlot(e.target.value)} className="w-full p-2.5 rounded-xl border border-slate-200 focus:border-blue-500 outline-none">
                    <option value="">Select Time...</option>
                    {TIME_SLOTS.map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Teacher Name</label>
                  <input required type="text" value={teacherName} onChange={e => setTeacherName(e.target.value)} placeholder="e.g. Rahul Kumar" className="w-full p-2.5 rounded-xl border border-slate-200 focus:border-blue-500 outline-none" />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Google Meet URL</label>
                  <input required type="url" value={meetLink} onChange={e => setMeetLink(e.target.value)} placeholder="https://meet.google.com/..." className="w-full p-2.5 rounded-xl border border-slate-200 focus:border-blue-500 outline-none" />
                </div>

                <div className="flex gap-2 mt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className={`flex-1 py-3 text-white font-bold rounded-xl transition-colors disabled:opacity-50 ${
                      editingBatchId ? "bg-indigo-600 hover:bg-indigo-700" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {submitting ? "Saving..." : editingBatchId ? "Update Batch" : "Save Batch"}
                  </button>
                  {editingBatchId && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BatchManager;
